import { useEffect, useState } from 'react';

/**
 * Hook that detects iOS (iPhone/iPad/iPod) and iPadOS,
 * including iPadOS pretending to be macOS Safari ("Request Desktop Website").
 *
 * @returns {boolean | null} - true if iOS/iPadOS is detected, false otherwise, null initially.
 */
export default function useDetectIos(): boolean | null {
  const [isIOS, setIsIOS] = useState<boolean | null>(null);

  useEffect(() => {
    // Ensure this only runs on the client side where window and navigator are available
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      setIsIOS(false); // Cannot detect on server side
      return;
    }

    // --- Helper function to check for touch support ---
    function hasTouchSupport(): boolean {
      // navigator.maxTouchPoints is the modern way (and detects > 0 touch points)
      if (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) {
        return true;
      }
      // Fallback for older WebKit based browsers
      // Should be redundant with maxTouchPoints but good as a belt-and-suspenders check
      if ('ontouchstart' in window) {
        return true;
      }
      // Check media queries for pointer type (coarse means touch)
      if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
        return true;
      }
      // Special check for some older browsers/devices might be needed here
      // but maxTouchPoints covers most modern iOS/iPadOS
      return false;
    }
    // --- End Helper Function ---

    const ua = navigator.userAgent;
    const { platform } = navigator; // Get platform info

    // 1. Standard check for iOS/iPadOS/iPod mobile User Agents
    // Added iPod for completeness. Added check against MSStream to avoid rare WinPhone false positives.
    const isMobileIOS = /iPad|iPhone|iPod/.test(ua) && !('MSStream' in window);

    // 2. Check for iPadOS in "Desktop Mode" (mimicking macOS Safari)
    let isDesktopModeIPad = false;
    if (
      /Macintosh/i.test(ua) && // Contains "Macintosh" (like macOS)
      navigator.maxTouchPoints &&
      navigator.maxTouchPoints > 0 && // **Crucially**, has touch points
      !/Mobile/i.test(ua) && // Does NOT contain "Mobile"
      /Safari/i.test(ua) && // Contains "Safari"
      !/Chrome|CriOS|FxiOS|EdgiOS/i.test(ua) // Exclude Chrome/Firefox/Edge iOS wrappers which might also contain Safari/Mac
    ) {
      // Additional check for platform can increase confidence, but touch + Mac-like UA is strong
      if (platform === 'MacIntel' || platform === 'MacPPC' || platform.startsWith('Mac')) {
        // Further confirmation it's trying to look like a Mac
        isDesktopModeIPad = true;
      } else if (!platform) {
        // If platform is undefined but other checks pass, still likely iPad
        isDesktopModeIPad = true;
      } else {
        // It claims to be Macintosh in UA, has Touch, but platform is something else?
        // Still likely iPadOS desktop mode more than a touch-enabled Mac.
        isDesktopModeIPad = true;
      }
    }
    // Alternative simpler check combining platform and touch:
    // Many sources report iPadOS desktop mode sets platform to 'MacIntel'.
    // Combining this with touch support is a very reliable signal.
    if (!isDesktopModeIPad && platform === 'MacIntel' && hasTouchSupport()) {
      isDesktopModeIPad = true;
    }

    // Set state: True if it's either standard Mobile iOS or Desktop Mode iPadOS
    setIsIOS(isMobileIOS || isDesktopModeIPad);
  }, []); // Empty dependency array ensures this runs only once on mount

  return isIOS;
}
